<script setup lang="ts">
/**
 * One-time „podmínky" prompt for users who have not accepted the current terms
 * version (existing accounts, admin-created accounts, a TERMS_VERSION bump).
 * Shown by SidebarLayout while authStore.termsAccepted is false; the only way
 * out is accepting (POST /api/auth/accept-terms) — or logging out.
 * Teleported to <body> like FeedbackModal (the toolbar's backdrop-filter would
 * clip a fixed overlay).
 */
import { LEGAL_LINKS } from '~/composables/useLegal'
import { useAuthStore } from '~/store/auth'
import { authedFetch } from '~/utils/authed-fetch'
import { getApiBase } from '~/utils/api-base'

const authStore = useAuthStore()
const busy = ref(false)
const error = ref('')

async function accept() {
  busy.value = true
  error.value = ''
  try {
    const res = await authedFetch(`${getApiBase()}/api/auth/accept-terms`, { method: 'POST' })
    if (!res.ok) throw new Error(String(res.status))
    authStore.termsAccepted = true
  } catch {
    error.value = 'Nepodařilo se uložit, zkuste to prosím znovu.'
  } finally {
    busy.value = false
  }
}

function logout() {
  authStore.logout()
  navigateTo('/auth/login')
}
</script>

<template>
  <Teleport to="body">
    <div class="tam-overlay" role="dialog" aria-modal="true" aria-labelledby="tam-title">
      <div class="tam-card">
        <h2 id="tam-title" class="wb-display tam-h">Obchodní podmínky</h2>
        <p class="tam-p">
          Sepsali jsme pravidla služby qCMS: jak funguje zkušební doba a placení, co se děje s vaším webem a daty a jak
          nakládáme s údaji návštěvníků vašeho webu.
        </p>
        <ul class="tam-links">
          <li><a :href="LEGAL_LINKS.terms" target="_blank" rel="noopener">Obchodní podmínky</a></li>
          <li>
            <a :href="LEGAL_LINKS.dpa" target="_blank" rel="noopener">Zpracovatelská smlouva</a> (součást podmínek)
          </li>
          <li><a :href="LEGAL_LINKS.privacy" target="_blank" rel="noopener">Ochrana osobních údajů</a></li>
        </ul>
        <p v-if="error" class="tam-err">{{ error }}</p>
        <div class="tam-actions">
          <button type="button" class="wb-btn accent" :disabled="busy" @click="accept">
            {{ busy ? 'Ukládám…' : 'Souhlasím s podmínkami' }}
          </button>
          <button type="button" class="tam-out" @click="logout">Odhlásit se</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.tam-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: color-mix(in srgb, var(--ink) 45%, transparent);
}
.tam-card {
  width: 100%;
  max-width: 460px;
  background: var(--paper);
  border-radius: var(--r-xl);
  padding: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
.tam-h {
  font-size: 28px;
  margin: 0 0 10px;
}
.tam-p {
  margin: 0 0 14px;
  font-size: 15px;
  line-height: 1.55;
  color: var(--ink-2);
}
.tam-links {
  margin: 0 0 20px;
  padding-left: 18px;
  font-size: 15px;
  line-height: 1.8;
}
.tam-links a {
  color: var(--accent);
}
.tam-err {
  margin: 0 0 12px;
  color: #a3352b;
  font-size: 14px;
}
.tam-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.tam-out {
  font: inherit;
  font-size: 14px;
  background: none;
  border: none;
  color: var(--ink-3);
  cursor: pointer;
  text-decoration: underline;
}
</style>
